import React, { useEffect, useState } from 'react';
import {
  AddCircleOutline,
  ArrowBackIos,
  Cached,
  EmojiObjects,
  QueuePlayNext
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Header } from '../../components/header/header';
import { viewRightSideBarEnum } from '../../constants/sidebarEnums';
import Strings from '../../i18n';
import { DeviceListByLocationTemplate } from '../../templates/locations/devicesByLocation/deviceListByLocation/deviceListByLocation';
import { ButtonIconStyled } from '../devices/devicesStyles';
import {
  StyledBackButtonNavigator,
  StyledHeaderContainer,
  StyledTitleHeader
} from '../../components/layout/main/mainStyles';
import { ReOpenSideBarIcon } from '../../components/commons/reOpenSideBarIcon/reOpenSideBarIcon';
import { DeviceByLocation } from '../../types/Device/DeviceInterfaces';
import { useRefreshStatusDevicesMutation } from '../../services/device/deviceService';
import {
  useIncrementAllTablesVersionMutation,
  useResetAllTablesVersionMutation
} from '../../services/locations/locationsService';
import { useSendTurnOffDevicesCommandMutation } from '../../services/commandsService/commandsService';

export const DeviceByLocationView = () => {
  const { locationId } = useParams();
  const [searchParams] = useSearchParams();
  const accessPointId = searchParams.get('accessPointId');
  const ssidName = searchParams.get('ssidName');
  const [refreshDevices, setRefreshDevices] = useState(false);
  const [openRightSideBar, setOpenRightSideBar] = useState(false);
  const [turnOffDevices, { isSuccess: isSuccessTurnOffDevices }] =
    useSendTurnOffDevicesCommandMutation();
  const [onlineAndTotalDevices, setOnlineAndTotalDevices] = useState<
    { online: number; total: number } | undefined
  >(undefined);
  const navigate = useNavigate();
  const [sideBarOpenFor, setSideBarOpenFor] = useState(
    viewRightSideBarEnum.addDevice
  );
  const openRightSideBarForMethod = (method: number) => {
    setSideBarOpenFor(method);
    setOpenRightSideBar(true);
  };

  const [refreshStatusDevices, { isSuccess: isSuccessRefreshStatus }] =
    useRefreshStatusDevicesMutation();

  const [
    incrementAllTablesVersion,
    { isSuccess: isSuccessIncrementAllTablesVersion }
  ] = useIncrementAllTablesVersionMutation();

  const [resetAllTablesVersion, { isSuccess: isSuccessResetAllTablesVersion }] =
    useResetAllTablesVersionMutation();

  const [selectedDevices, setSelectedDevices] = useState<DeviceByLocation[]>(
    []
  );

  const refreshStatusHandler = () => {
    if (locationId) {
      refreshStatusDevices(+locationId);
    }
  };

  const incrementAllTablesVersionHandler = () => {
    if (locationId) {
      incrementAllTablesVersion(+locationId);
    }
  };

  const resetAllTablesVersionHandler = () => {
    if (locationId) {
      resetAllTablesVersion(+locationId);
    }
  };

  useEffect(() => {
    if (isSuccessRefreshStatus) {
      toast.success(Strings.serverAccess.devices.successRefreshStatus);
      setRefreshDevices(true);
    }
  }, [isSuccessRefreshStatus]);

  useEffect(() => {
    if (isSuccessIncrementAllTablesVersion) {
      toast.success(
        Strings.serverAccess.devices.successIncrementAllTablesVersion
      );
    }
  }, [isSuccessIncrementAllTablesVersion]);

  useEffect(() => {
    if (isSuccessResetAllTablesVersion) {
      toast.success(Strings.serverAccess.devices.successResetAllTablesVersion);
    }
  }, [isSuccessResetAllTablesVersion]);

  const [searchValue, setSearchValue] = useState('');
  const redirectBtn = () => {
    if (accessPointId) {
      navigate(`/locations/${locationId}/accessPoints`);
    } else {
      navigate(`/locations/${locationId}/applications`);
    }
  };
  const turnOffDevicesHandler = () => {
    if (locationId) {
      const payload = {
        id: +locationId,
        type: 'Location'
      };
      turnOffDevices(payload);
    }
  };
  useEffect(() => {
    if (isSuccessTurnOffDevices) {
      toast.success(Strings.serverAccess.devices.successTurnOffDevices);
    }
  }, [isSuccessTurnOffDevices]);

  return (
    <div>
      <Header>
        <StyledHeaderContainer>
          <div className="row justify-content-between">
            <div className="col-4 text-left">
              <StyledBackButtonNavigator size="small" onClick={redirectBtn}>
                <ArrowBackIos />
              </StyledBackButtonNavigator>
            </div>
            <div className="col-4 text-center">
              <StyledTitleHeader>
                {Strings.devices.devices}
                {onlineAndTotalDevices && (
                  <span>
                    {ssidName && accessPointId
                      ? ` (${ssidName})`
                      : ` (${onlineAndTotalDevices.online} / ${onlineAndTotalDevices.total})`}
                  </span>
                )}
              </StyledTitleHeader>
            </div>
            <div className="col-4 text-right">
              <ButtonIconStyled
                disabled
                size="small"
                onClick={() => {
                  openRightSideBarForMethod(
                    viewRightSideBarEnum.addDeviceToLocation
                  );
                }}
              >
                <AddCircleOutline />
              </ButtonIconStyled>
              <Tooltip title={Strings.serverAccess.devices.refreshStatus}>
                <ButtonIconStyled
                  size="small"
                  onClick={() => {
                    refreshStatusHandler();
                  }}
                >
                  <Cached />
                </ButtonIconStyled>
              </Tooltip>
              <Tooltip
                title={Strings.serverAccess.devices.incrementAllTablesVersion}
              >
                <ButtonIconStyled
                  size="small"
                  onClick={() => {
                    incrementAllTablesVersionHandler();
                  }}
                >
                  <QueuePlayNext />
                </ButtonIconStyled>
              </Tooltip>
              <Tooltip
                title={Strings.serverAccess.devices.restAllTablesVersion}
              >
                <ButtonIconStyled
                  size="small"
                  onClick={() => {
                    resetAllTablesVersionHandler();
                  }}
                >
                  <RestartAltIcon />
                </ButtonIconStyled>
              </Tooltip>
              <Tooltip title={Strings.serverAccess.devices.turnOffDevices}>
                <ButtonIconStyled
                  size="small"
                  onClick={() => {
                    turnOffDevicesHandler();
                  }}
                >
                  <EmojiObjects />
                </ButtonIconStyled>
              </Tooltip>
              <ReOpenSideBarIcon
                isOpenRightSideBar={openRightSideBar}
                selectedItems={selectedDevices}
                singleItemMethod={() => {
                  openRightSideBarForMethod(viewRightSideBarEnum.details);
                }}
                multipleItemsMethod={() => {
                  openRightSideBarForMethod(
                    viewRightSideBarEnum.editMultipleDevices
                  );
                }}
              />
            </div>
          </div>
        </StyledHeaderContainer>
      </Header>
      <DeviceListByLocationTemplate
        searchValue={searchValue}
        openRightSideBar={openRightSideBar}
        setOpenRightSideBar={setOpenRightSideBar}
        selectedDevices={selectedDevices}
        setSelectedDevices={setSelectedDevices}
        sideBarOpenFor={sideBarOpenFor}
        setSideBarOpenFor={setSideBarOpenFor}
        refreshDevices={refreshDevices}
        setRefreshDevices={setRefreshDevices}
        setOnlineAndTotalDevices={setOnlineAndTotalDevices}
        accessPointId={accessPointId ? Number(accessPointId) : null}
      />
    </div>
  );
};
